import axios from "axios";

/*
 * The trades (yrkesgrupper) a company groups its technicians into: /api/trades.
 *
 * A grouping — electricians, mechanics, an outside contractor. A trade decides nothing about who
 * sees what. What it does decide (AB#1624) is who hears about a work order: the departments mapped
 * to it send their notifications to its members instead of to the department's.
 *
 * Shaped like cmmsSparePartType.jsx, down to throwResponseData — the CMMS controllers answer a
 * failure with ApiErrorResponse { code, message }, and rethrowing the body rather than the axios
 * error is what lets formatCmmsError print "A trade named 'El' already exists for this company"
 * instead of "Request failed with status code 400". A duplicate name has its own code,
 * CMMS_TRADE_NAME_TAKEN, and adding someone outside the technician tier has another,
 * CMMS_NOT_A_TECHNICIAN.
 *
 * Nothing here sends a user id for the author. The server takes "who did this" from the token and
 * overwrites anything a request supplied, so passing one would only suggest it mattered.
 */
const baseUrl = "/api/trades";

const throwResponseData = (error) => {
  throw error.response?.data ?? error;
};

/*
 * Each trade arrives with its members and its departments, so the settings list needs no second
 * read to show who is in what and where it works.
 */
export const getTrades = async (companyKey, includeInactive = false) => {
  try {
    const response = await axios.get(baseUrl, { params: { companyKey, includeInactive } });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

/*
 * No caller yet, for the same reason getSparePartTypeById has none: kept so the fetcher maps the
 * whole API rather than the part of it the settings page happens to use.
 */
export const getTradeById = async (companyKey, id) => {
  try {
    const response = await axios.get(`${baseUrl}/${id}`, { params: { companyKey } });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

export const createTrade = async (trade) => {
  try {
    const response = await axios.post(baseUrl, trade);
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

/*
 * A rename, in place. The body carries no isActive and no members: retiring a trade and changing
 * who is in it each have an endpoint of their own, not a field an edit to the name could change in
 * passing.
 */
export const updateTrade = async (id, trade) => {
  try {
    const response = await axios.put(`${baseUrl}/${id}`, trade);
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

/*
 * Retire, not delete. There is no DELETE endpoint on purpose: work orders are going to name these
 * rows. A retired trade keeps its members and its departments, but routes nothing until restored.
 *
 * Answers 204, so response.data is empty — returned anyway to keep the fetchers shaped alike.
 */
export const deactivateTrade = async (companyKey, id) => {
  try {
    const response = await axios.post(`${baseUrl}/${id}/deactivate`, null, {
      params: { companyKey },
    });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

export const activateTrade = async (companyKey, id) => {
  try {
    const response = await axios.post(`${baseUrl}/${id}/activate`, null, {
      params: { companyKey },
    });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

/*
 * Replace-set: the ids sent are the members after the call, so an empty list empties the trade.
 * There is no add-one or remove-one — the dialog holds the whole list and sends it back.
 *
 * Only the ids being added are checked against the technician tier. Someone whose role was taken
 * away after they joined stays sendable, so a trade with one stale member can still be saved.
 *
 * Answers with the whole trade, members included.
 */
export const replaceTradeMembers = async (companyKey, id, userIds) => {
  try {
    const response = await axios.put(`${baseUrl}/${id}/members`, { companyKey, userIds });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

/*
 * The departments a trade serves, replace-set like the members: the ids sent are the mapping after
 * the call, and an empty list unmaps the trade everywhere.
 *
 * Only the ids being added are checked. They must be active Department nodes of the company, and
 * anything else has a code of its own, CMMS_NOT_A_DEPARTMENT_NODE. A mapped department that has
 * since been deactivated stays sendable, for the same reason a stale member does.
 *
 * Answers with the whole trade, departments included.
 */
export const replaceTradeDepartments = async (companyKey, id, assetNodeIds) => {
  try {
    const response = await axios.put(`${baseUrl}/${id}/departments`, { companyKey, assetNodeIds });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};
